import React from "react"
import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import { FlagsIconSeeker, IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"
import AppBrandMark from "@components/DataBlocks/BrandMark"
import { displayName, marketName } from "./@services/identity"


const actionSx = {
	width: 20, height: 20, p: 0, borderRadius: '5px', color: dsColorNew.textMuted,
	'&:hover': { color: dsColorNew.appBlue, backgroundColor: dsColorNew.hoverBgLight },
}

/**
 * A brand or a site of the identity: its logo (and, for a site, its flag and country code), its name,
 * the *Primary* tag on the first brand, and real buttons — ☆ *Make primary* (only when `onMakePrimary`
 * comes: brands) and ✕ *Remove* (UX D4: MUI's chips only bring ✕). `readOnly` drops both (Iris's "Who
 * you are" mirror, CU-869fkmt6r). Sites have no primary (CU-869fmj51h): a named site without a country
 * shows "No country"; an unnamed one shows no country at all (UX Obs. 7). A null `label` shows "Unknown brand" /
 * "Unknown site", muted.
 */
export default function AppIdentityChip({ id, kind = 'brand', label, market, marketName: mNameIn, primary = false, readOnly = false, onMakePrimary, onRemove, disabled = false }) {
	const name = displayName(label ?? null, id, kind)
	const mName = kind === 'site' ? (mNameIn || marketName(market)) : null
	const showCountry = kind === 'site' && !!market && label != null
	const noCountry = kind === 'site' && !market && label != null
	const ariaLabel = kind !== 'site' || label == null ? undefined : `${name}, ${market ? (mName || market) : 'no country'}`

	return <Box component="span" aria-label={ariaLabel} sx={{
		display: 'inline-flex', alignItems: 'center', gap: 0.75, minHeight: 28, maxWidth: '100%',
		pl: 1, pr: readOnly ? 1 : 0.5, borderRadius: '6px',
		border: `1px solid ${primary ? dsColorNew.appBlue : 'transparent'}`,
		backgroundColor: primary ? dsColorNew.white : dsColorNew.pillActiveBg,
		color: dsColorNew.textTitle,
	}}>
		<AppBrandMark name={label || ''}/>
		{showCountry && <FlagsIconSeeker countryName={market}/>}
		<Typography component="span" sx={{
			fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
			color: label ? dsColorNew.textTitle : dsColorNew.textMuted,
		}}>{name}</Typography>
		{showCountry && <Typography component="span" title={mName || market} sx={{
			fontSize: 11, fontFamily: 'monospace', color: dsColorNew.textMuted,
		}}>{market}</Typography>}
		{noCountry && <Typography component="span" title="No country on record for this site" sx={{
			fontSize: 11, color: dsColorNew.textMuted, px: 0.5, borderRadius: '4px', border: `1px dashed ${dsColorNew.textMuted}`, whiteSpace: 'nowrap',
		}}>No country</Typography>}
		{primary && <Box component="span" sx={{
			display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.15, borderRadius: '4px',
			backgroundColor: dsColorNew.appBlue, color: dsColorNew.white, fontSize: 11, fontWeight: 600,
		}}>
			<IconSeeker icon="star" iconWeight="solid" sx={{ fontSize: 9 }}/>Primary
		</Box>}
		{!readOnly && !primary && !!onMakePrimary && <Tooltip title="Make primary" arrow>
			<span><IconButton aria-label={`Make ${name} primary`} disabled={disabled} onClick={onMakePrimary} sx={actionSx}>
				<IconSeeker icon="star" iconWeight="regular" sx={{ fontSize: 11 }}/>
			</IconButton></span>
		</Tooltip>}
		{!readOnly && <Tooltip title={primary ? `Remove ${name} — the next one becomes primary` : 'Remove'} arrow>
			<span><IconButton aria-label={`Remove ${name}`} disabled={disabled} onClick={onRemove} sx={{
				...actionSx, '&:hover': { color: dsColorNew.dangerRed, backgroundColor: dsColorNew.dangerBg },
			}}>
				<IconSeeker icon="xmark" sx={{ fontSize: 11 }}/>
			</IconButton></span>
		</Tooltip>}
	</Box>
}
